import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import Styled from 'styled';
import Helmet from 'helmet/helmet';

import ScrollToTop from 'lib/scroll/scroll-to-top';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import { URLBuilderLink } from 'lib/url/url-builder-link';
import Localizable from 'lib/intl/localizable';

import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import TitleView from 'four-nets/ui/title-view';
import GalleryView from 'four-nets/ui/gallery/gallery-view';
import SplitView from 'four-nets/ui/layout/split-view';
import { BreadcrumbsViewModel } from 'four-nets/ui/breadcrumbs-view';
import EnhancedText from 'four-nets/ui/enhanced-text/enhanced-text';
import FacebookButtonView from 'four-nets/ui/facebook-button/facebook-button-view';
import { HomepageLink } from 'four-nets/homepage/homepage-link';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import CreateComplaintView, { ComplaintForm } from 'four-nets/bazaar/complaint/create-complaint-view';
import { CategoryLink } from 'four-nets/bazaar/categories/category-link';
import { ProductLink } from 'four-nets/bazaar/product/product-link';
import ProductStatusBarView from 'four-nets/bazaar/product/detail/product-status-bar-view';
import ProductActionsBarView from 'four-nets/bazaar/product/detail/product-actions-bar-view';
import ProductPriceHistoryStackView from 'four-nets/bazaar/product/detail/product-price-history-stack-view';
import BuyersView from 'four-nets/bazaar/product/negotiation/buyers-view';
import BazaarProfileLoader from 'four-nets/bazaar/profile/bazaar-profile-loader';
import { Site } from 'four-nets/site';
import { SearchApi } from 'four-nets/search/search-api';
import { HideForUnpaidVendor } from 'four-nets/vendor/permissions';
import isPrArticleUsername from 'four-nets/common/is-pr-article-username';
import { Product, Category, Buyer, SuperCategoryStore, ProductStore } from 'four-nets/bazaar/bazaar';
import { UserStore } from 'four-nets/user/user';
import picture from 'four-nets/icons/svg/icon_picture.svg';
import alert from 'four-nets/icons/svg/icon_alert-outlined.svg';

import RelatedProducts from './related-products/related-products';

import splitStyles from './negotiation/split-view.scss';
import splitStylesNoSide from './negotiation/split-no-side-view.scss';
import photoswipeGalleryStyles from './detail/photoswipe-gallery-view.scss';
import complainIconStyles from './product-view-complainicon.scss';
import noPhotoIconStyles from './product-view-nophotoicon.scss';
import flexColumnBazaarProfilePureViewStyles from './bazaar-profile-pure-view-profile.scss';
import defaultStyles from './product-view.scss';

export interface Props {
    styles: typeof defaultStyles;
    userStore: UserStore;
    product: Product;
    category: Category;
    productBuyer?: Buyer;
    loadingMessages?: boolean;
    productStore: ProductStore;
    superCategoryStore: SuperCategoryStore;
    forceUpdate: () => void;
    intl: any;
    searchApi: SearchApi;
}

export interface State {
    identifiers: Buyer[];
    contentHidden: boolean;
    replyRef?: any;
    priceStackViewOpen: boolean;
}

@Localizable()
@observer
class ProductView extends React.Component<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
        authCheck: PropTypes.func,
    };

    createComplaintView: any;
    priceHistoryRef: any;
    breadcrumbsViewModel: any;
    productStatusBarViewModel: any;
    productActionsBarViewModel: any;
    disposer: any;

    constructor(props: Props) {
        super(props);

        const { product, category, intl } = props;

        this.breadcrumbsViewModel = new BreadcrumbsViewModel({
            links: [
                new HomepageLink(),
                new URLBuilderLink({
                    url: 'bazaar',
                    label: intl.formatMessage(defineMessages({ BAZAAR: { id: 'BAZAAR' } }).BAZAAR),
                }),
                new CategoryLink({ category }),
                new ProductLink({ product }),
            ],
        });

        this.state = {
            identifiers: [],
            contentHidden: props.productBuyer ? true : false,
            priceStackViewOpen: false,
        };
    }

    openPriceStackViewOpen = () => {
        this.setState({ priceStackViewOpen: true });
    };

    closePriceStackViewOpen = () => {
        this.setState({ priceStackViewOpen: false });
    };

    setCreateComplaintView = (ref: any) => {
        this.createComplaintView = ref;
    };

    setReplyRef = (ref: any) => {
        this.setState({ replyRef: ref });
    };

    renderHelmet = (H: React.ComponentClass) => {
        const { product } = this.props;
        const { site } = this.context;
        const hasPicture = product.photofiles && product.photofiles.length > 0;

        return (
            <FormattedMessage id='BAZAAR_SEO_WEDDING'>
                {bazaarSeoWeddingMsg => (
                    <FormattedMessage id='BAZAAR_LINK_LABEL'>
                        {bazaarLinkLabelMsg => (
                            <H>
                                <title>{`${product.title} - ${
                                    site.isWedding ? bazaarSeoWeddingMsg : bazaarLinkLabelMsg
                                } | ${site.name}`}</title>
                                <meta name='description' content={product.description} />
                                <meta
                                    property='og:title'
                                    content={`${site.isWedding ? bazaarSeoWeddingMsg : bazaarLinkLabelMsg} - ${
                                        product.title
                                    }`}
                                />
                                <meta property='og:url' content={product.href} />
                                <meta property='og:image' content={String(hasPicture && product.photofiles[0].src)} />
                                <meta
                                    property='og:image:width'
                                    content={String(hasPicture && product.photofiles[0].width)}
                                />
                                <meta
                                    property='og:image:height'
                                    content={String(hasPicture && product.photofiles[0].height)}
                                />
                                <meta property='og:type' content='product.item' />
                                <meta property='og:description' content={product.description} />
                            </H>
                        )}
                    </FormattedMessage>
                )}
            </FormattedMessage>
        );
    };

    complain = () => {
        const { createComplaintView } = this;

        if (createComplaintView) {
            const showCreateComplaintView = () => {
                createComplaintView.show({
                    form: new ComplaintForm({
                        handleSubmit: ({ comment }: { comment: string }) => {
                            return this.props.product.complain(comment);
                        },
                    }),
                });
                return Promise.resolve();
            };
            this.context.authCheck(showCreateComplaintView, AuthOrigin.NewComplain);
        }
        return Promise.resolve();
    };

    loadIdentifiers() {
        if (this.isAuthorized()) {
            return this.props.product.loadBuyerIdentifiers().then((identifiers: any) => {
                this.setState({ identifiers });
            });
        }
        return null;
    }

    componentDidMount() {
        this.props.product.increaseViewsCount();
        this.disposer = reaction(
            () => this.props.userStore.me,
            () => this.loadIdentifiers(),
            { fireImmediately: true }
        );
    }

    componentWillUnmount() {
        this.disposer && this.disposer();
    }

    showPriceHistory = () => {
        this.setState({ priceStackViewOpen: true });
    };

    contextMenuDisplayed = () => {
        const { identifiers } = this.state;
        return identifiers && identifiers.length > 0 && this.isAuthorized();
    };

    get mainContent() {
        let mainContent = null;
        if (this.contextMenuDisplayed() && !this.props.productBuyer) {
            mainContent = (
                <div>
                    <h2 className={this.props.styles.secondaryTitle}>
                        <FormattedMessage id='BAZAAR_CONVERSATIONS' />
                    </h2>
                    <BuyersView identifiers={this.state.identifiers} />
                </div>
            );
        }
        return mainContent;
    }

    get sideContent() {
        let authorSideContent = null;
        if (!this.isAuthor()) {
            authorSideContent = (
                <div className={this.props.styles.fullWidth}>
                    <h2 className={this.props.styles.heading}>
                        <FormattedMessage id='BAZAAR_SELLER' />
                    </h2>
                    <BazaarProfileLoader
                        profileStyle={{ profile: flexColumnBazaarProfilePureViewStyles.profile }}
                        shopInfoTitle={<FormattedMessage id='BAZAAR_USER_RULES' />}
                        noShopInfo={<FormattedMessage id='BAZAAR_NO_SHOP_INFO_USER' />}
                        hideEdit={true}
                        user={this.props.product.user}
                    />
                </div>
            );
        }

        let buyersSideContent = null;
        if (this.contextMenuDisplayed() && this.props.productBuyer) {
            buyersSideContent = (
                <div className={this.props.styles.fullWidth}>
                    <h2 className={this.props.styles.secondaryTitle}>
                        <FormattedMessage id='BAZAAR_BUYERS' />
                    </h2>
                    <BuyersView
                        current={this.props.productBuyer && this.props.productBuyer.username}
                        column={true}
                        identifiers={this.state.identifiers}
                    />
                </div>
            );
        }
        const sideContent =
            authorSideContent || buyersSideContent ? (
                <div className={this.props.styles.fullWidth}>
                    <div className={this.props.styles.divider} />
                    {authorSideContent}
                    {buyersSideContent}
                </div>
            ) : null;

        return sideContent;
    }

    isAuthorized = () => {
        const isAuthor = this.isAuthor();
        const isSuperstaff = this.isSuperstaff();
        return isAuthor || isSuperstaff;
    };

    isBuyer = () => {
        const { productBuyer } = this.props;
        return !this.isAuthor() && productBuyer;
    };

    isAuthor = () => {
        const { userStore, product } = this.props;
        const user = userStore && userStore.me;
        return user && product && user === product.user;
    };

    isSuperstaff = () => {
        const { userStore } = this.props;
        return userStore.me && userStore.me.isSuperstaff;
    };

    showContent = () => {
        this.setState({ contentHidden: false });
    };

    render() {
        const { product, loadingMessages, productStore, productBuyer, superCategoryStore, searchApi } = this.props;
        const styles = this.props.styles;

        const hasPicture = product.photofiles && product.photofiles.length > 0;
        const imageClassNames = [styles.imageWrapper];
        const columnsWrapperClassNames = [styles.columnsWrapper];

        if (!hasPicture) {
            imageClassNames.push(styles.centerer);
        }

        const hasPriceHistory = product.priceHistory && product.priceHistory.length > 0;
        const priceTagClassNames = [styles.priceTag];
        if (hasPriceHistory) {
            priceTagClassNames.push(styles.pointer);
        }

        const titleClasses = [styles.title];
        if (product.isHidden) {
            titleClasses.push(styles.deleted);
        }

        const priceFormatter = Intl.NumberFormat();

        const priceToShow = !product.price
            ? null
            : priceFormatter
                  .formatToParts(product.price)
                  .map(part => (part.type === 'group' ? { type: 'group', value: ' ' } : part))
                  .map(part => part.value)
                  .join('');

        const titlePart = (
            <div className={styles.titlePart}>
                <ProductPriceHistoryStackView
                    priceHistory={product.priceHistory}
                    open={this.state.priceStackViewOpen}
                    onClose={this.closePriceStackViewOpen}
                />
                <TitleView
                    title={
                        <span className={styles.titleWrapper}>
                            <h1 className={titleClasses.join(' ')}>{product.title}</h1>
                            {product.isHidden ? (
                                <span className={styles.deletedMsg}>
                                    (<FormattedMessage id='DELETED_ENTRY' />)
                                </span>
                            ) : null}
                        </span>
                    }
                />
                <ProductStatusBarView product={product} />
                <button
                    className={priceTagClassNames.join(' ')}
                    onClick={this.showPriceHistory}
                    disabled={!hasPriceHistory}
                >
                    {product.price > 0 ? (
                        <span>
                            {priceToShow} {this.context.site.currency}
                        </span>
                    ) : (
                        <FormattedMessage id='PRICE_FORFREE' />
                    )}
                    {hasPriceHistory ? (
                        <span className={styles.history}>
                            (<FormattedMessage id='BAZAAR_PRICE_HISTORY' />)
                        </span>
                    ) : null}
                </button>
                {!product.isHidden && (this.isAuthorized() || productBuyer || this.state.replyRef) ? (
                    <HideForUnpaidVendor>
                        <ProductActionsBarView
                            product={product}
                            productStore={productStore}
                            superCategoryStore={superCategoryStore}
                            authorView={this.isAuthor()}
                            isSuperstaff={this.isSuperstaff()}
                            replyRef={this.state.replyRef}
                            contentHidden={this.state.contentHidden}
                            showContent={this.showContent}
                            forceUpdate={this.props.forceUpdate}
                        />
                    </HideForUnpaidVendor>
                ) : null}
            </div>
        );

        const galleryPartClasses = [styles.galleryPart];
        if (this.state.contentHidden) {
            galleryPartClasses.push(styles.displayNone);
        }
        const galleryPart = (
            <div className={galleryPartClasses.join(' ')}>
                {!hasPicture ? (
                    <div className={imageClassNames.join(' ')}>
                        <div className={styles.noPhoto}>
                            <SVGIcon style={{ image: noPhotoIconStyles.noPhotoIcon }} src={picture} />
                        </div>
                    </div>
                ) : null}
                {hasPicture ? (
                    <GalleryView
                        limit={4}
                        altPrefix={`${product.title} - `}
                        items={product.photofiles}
                        photoswipeGalleryStyles={photoswipeGalleryStyles}
                        firstImageSizes={{ xs: '95vw', md: 490, xlg: 540 }}
                    />
                ) : null}
                <button onClick={this.complain} className={`${styles.complainButton} ${styles.onlyDesktop}`}>
                    <SVGIcon style={{ image: complainIconStyles.complainIcon }} src={alert} />
                    <FormattedMessage id='BAZAAR_COMPLAIN' />
                </button>
            </div>
        );

        const descriptionPartClasses = [styles.descriptionPart];
        if (this.state.contentHidden) {
            descriptionPartClasses.push(styles.displayNone);
        }
        const descriptionPart = (
            <div className={descriptionPartClasses.join(' ')}>
                <CreateComplaintView innerRef={this.setCreateComplaintView} />
                <p className={styles.paragraph}>
                    <EnhancedText followLink={product.isVendor || isPrArticleUsername(product.user.username)}>
                        {product.description}
                    </EnhancedText>
                </p>
                <div className={styles.fbButtonWrapper}>
                    <FacebookButtonView />
                </div>
                <button onClick={this.complain} className={`${styles.complainButton} ${styles.onlyMobile}`}>
                    <SVGIcon style={{ image: complainIconStyles.complainIcon }} src={alert} />
                    <FormattedMessage id='BAZAAR_COMPLAIN' />
                </button>
            </div>
        );

        return (
            <div className={styles.pageWrapper}>
                <ScrollToTop product={product} />
                <div className={columnsWrapperClassNames.join(' ')}>
                    {titlePart}
                    {galleryPart}
                    {descriptionPart}
                </div>
                <SplitView
                    mainContent={
                        <LoadingWrapperView loading={loadingMessages}>
                            <div ref={this.setReplyRef} className={this.props.styles.divider} />
                            {this.props.children}
                            <div>{this.mainContent}</div>
                        </LoadingWrapperView>
                    }
                    sideContent={this.sideContent}
                    styles={this.sideContent ? splitStyles : splitStylesNoSide}
                />
                <RelatedProducts
                    searchApi={searchApi}
                    originalProductName={product.title}
                    originalProductId={product.id}
                />
                <Helmet render={this.renderHelmet} />
            </div>
        );
    }
}

export default Styled(defaultStyles)(ProductView);
